import { agentFetch } from '@/experiences/sales-portal/domain/superintelligence/blocs/services/agent-fetch';
import { HttpError } from '@platform/http/calyx-client';
import { superintelligenceEndpoint } from '@/experiences/sales-portal/domain/superintelligence/constants';
import type {
  AgUiInterrupt,
  UseAgUiThreadListAdapter,
} from '@assistant-ui/react-ag-ui';

export type ThreadStatus = 'regular' | 'archived';

type SwitchToThreadResult = Awaited<
  ReturnType<NonNullable<UseAgUiThreadListAdapter['onSwitchToThread']>>
>;

export type ThreadState = NonNullable<SwitchToThreadResult['state']>;

export interface ThreadRun {
  finishedAt: string | null;
  runId: string;
  startedAt: string;
  status: string;
}

export interface ThreadSummary {
  createdAt: string;
  lastRun: ThreadRun | null;
  status: ThreadStatus;
  threadId: string;
  title: string | null;
  updatedAt: string;
}

export interface ThreadPage {
  items: ThreadSummary[];
  nextCursor: string | null;
}

export interface ThreadDetail {
  createdAt: string;
  interrupts: AgUiInterrupt[];
  messages: unknown[];
  runInFlight: string | null;
  state: ThreadState;
  status: ThreadStatus;
  threadId: string;
  title: string | null;
  updatedAt: string;
}

export interface ThreadPatch {
  status?: ThreadStatus;
  title?: string;
}

export interface ThreadPatched {
  status: ThreadStatus;
  threadId: string;
  title: string | null;
}

export const THREAD_PAGE_SIZE = 20;

export const threadsEndpoint = (): string =>
  `${superintelligenceEndpoint()}/threads`;

export const threadEndpoint = (threadId: string): string =>
  `${threadsEndpoint()}/${encodeURIComponent(threadId)}`;

const toQuery = (params: Record<string, string | null | undefined>): string =>
  Object.entries(params)
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');

const request = async <T>(url: string, init: RequestInit = {}): Promise<T> => {
  const response = await agentFetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  if (!response.ok) {
    throw new HttpError(
      `Request failed with status ${response.status}`,
      response.status,
    );
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
};

export interface ListThreadsOptions {
  cursor?: string | null;
  signal?: AbortSignal;
  status: ThreadStatus;
}

export const listThreads = ({
  cursor,
  signal,
  status,
}: ListThreadsOptions): Promise<ThreadPage> =>
  request<ThreadPage>(
    `${threadsEndpoint()}?${toQuery({
      cursor,
      limit: String(THREAD_PAGE_SIZE),
      status,
    })}`,
    { signal },
  );

export const getThread = (
  threadId: string,
  signal?: AbortSignal,
): Promise<ThreadDetail> =>
  request<ThreadDetail>(threadEndpoint(threadId), { signal });

export const patchThread = (
  threadId: string,
  patch: ThreadPatch,
): Promise<ThreadPatched> =>
  request<ThreadPatched>(threadEndpoint(threadId), {
    body: JSON.stringify(patch),
    headers: { 'Content-Type': 'application/json' },
    method: 'PATCH',
  });

export const deleteThread = (threadId: string): Promise<void> =>
  request<void>(threadEndpoint(threadId), { method: 'DELETE' });
